<template>
    <div class="job-fit-result-content">
        <TabBar dark-scheme class="jfc-tabs">
            <TabItem
                v-for="tab in tabs"
                :key="tab.value"
                dark-scheme
                :label="tab.label"
                :active="tab.value === activeTab"
                @click="changeTab(tab.value)"
            />
        </TabBar>

        <div class="jfc-tab-content">

            <!-- Overview tab -->
            <div v-if="activeTab === 'overview'" class="overview-container">
                <div class="score-section">
                    <div class="score-summary">
                        <div>Job Fit Score:</div>
                        <div class="score">{{ averageScore }}%</div>
                    </div>
                    <div
                        class="score-wrapper"
                        @mouseenter="showScores = true"
                        @mouseleave="showScores = false"
                    >
                        <div class="score-bar">
                            <template v-if="!showScores">
                                <div
                                    class="score-fill"
                                    :style="{ width: averageScore + '%', backgroundColor: 'var(--volt)' }"
                                ></div>
                            </template>
                            <template v-else>
                                <div
                                    class="score-segment"
                                    v-for="(segment, index) in scoreSegments"
                                    :key="'seg-' + index"
                                    :style="{ width: segment.width + '%', backgroundColor: segment.color }"
                                />
                            </template>
                        </div>
                        <div class="score-details" v-if="showScores">
                            <div
                                v-for="(segment, index) in scoreSegments"
                                :key="'label-' + index"
                                class="score-label"
                            >
                                <span :style="{ backgroundColor: segment.color }" class="dot"></span>
                                <span>{{ segment.label }}</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="overview-section">
                    <div class="overview-container">
                        <div class="title">Top strengths</div>
                        <div class="list">
                            <div v-for="(item, i) in jobFitResult.portfolioOverview.strengths" :key="'strength-' + i" class="list-item">
                                <Icon :width="20" :height="20" icon-name="check" />
                                {{ item }}
                            </div>
                        </div>
                    </div>
                    <div class="overview-container">
                        <div class="title">Key gaps</div>
                        <div class="list">
                            <div v-for="(item, i) in jobFitResult.portfolioOverview.gaps" :key="'gap-' + i" class="list-item">
                                <Icon :width="20" :height="20" icon-name="minus" />
                                {{ item }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Breakdown tab -->
            <div v-if="activeTab === 'breakdown'" class="breakdown-container">
                <div class="title">Breakdown of results</div>
                <div class="accordion" v-for="section in allBreakdownItems" :key="section.projectShortId">
                    <div
                        @click="() => toggleSection(section.projectShortId)"
                        :class="['accordion-title', { 'open': openedSection === section.projectShortId }]"
                    >
                        <div class="page-name">{{ section.name }} ({{ section.gaps.length }})</div>
                        <Icon :icon-name="openedSection === section.projectShortId ? 'chevron-up' : 'chevron-down'" />
                    </div>
                    <div class="accordion-body" v-if="openedSection === section.projectShortId">
                        <div v-if="section.strengths.length">
                            <div class="title">Top strengths</div>
                            <div class="list">
                                <div v-for="(item, i) in section.strengths" :key="'s-' + i" class="list-item">
                                    <Icon :width="20" :height="20" icon-name="check" />
                                    {{ item }}
                                </div>
                            </div>
                        </div>
                        <div v-if="section.gaps.length">
                            <div class="title">Key gaps</div>
                            <div class="list">
                                <div v-for="(gap, i) in section.gaps" :key="'g-' + i" class="list-item">
                                    <Icon :width="20" :height="20" icon-name="minus" />
                                    {{ gap.label }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Job Description tab -->
            <div v-if="activeTab === 'jobDescription'" class="job-description-container">
                <div class="title">Job description</div>
                <div class="job-description-container">
                    <div class="job-title-and-description">
                        <div class="text-box">{{ jobFitResult.jobTitle }}</div>
                        <div class="text-box">{{ jobFitResult.jobCompany }}</div>
                    </div>
                    <div class="text-box">{{ jobFitResult.jobDescription }}</div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import TabBar from "../../../common/components/TabBar.vue";
import TabItem from "../../../common/components/TabItem.vue";
import Icon from "../../../common/Icon.vue";

export default {
    name: 'JFCResult',
    components: {Icon, TabItem, TabBar},
    props: ['jobFitResult'],
    data() {
        return {
            activeTab: 'overview',
            tabs: [
                {value: 'overview', label: 'Overview'},
                {value: 'breakdown', label: 'Breakdown'},
                {value: 'jobDescription', label: 'Job Description'},
            ],
            openedSection: '',
            showScores: false,
        };
    },
    computed: {
        averageScore() {
            const score = this.jobFitResult.jobFitScore;
            return Math.round((score.hardSkills + score.experience + score.toolsSoftware + score.industryExperience) / 4);
        },
        scoreSegments() {
            const score = this.jobFitResult.jobFitScore;
            const total = score.hardSkills + score.experience + score.toolsSoftware + score.industryExperience;

            return [
                { label: 'Hard skills', value: score.hardSkills, color: '#7C9300' },
                { label: 'Experience', value: score.experience, color: '#AFCE00' },
                { label: 'Tools/software', value: score.toolsSoftware, color: '#E7FF64' },
                { label: 'Industry experience', value: score.industryExperience, color: '#F7FFCD' }
            ].map(segment => ({
                ...segment,
                width: (segment.value / total) * this.averageScore
            }));
        },
        allBreakdownItems() {
            return [
                ...(this.jobFitResult.portfolioBreakdown.pages || []),
                ...(this.jobFitResult.portfolioBreakdown.caseStudies || [])
            ];
        }
    },
    methods: {
        changeTab(name) {
            this.activeTab = name;
        },
        toggleSection(id) {
            this.openedSection = this.openedSection === id ? '' : id;
        }
    }
};
</script>

<style scoped lang="scss">
.jfc-tabs {
    padding: 0 24px;
}

.jfc-tab-content {
    padding: 24px;

    .overview-container {
        font: var(--editor-ui-h2);
        color: var(--asphalt15);

        .score-section {
            display: flex;
            flex-direction: column;
            gap: 16px;
            padding-bottom: 36px;
            border-bottom: 1px solid var(--asphalt80);
            position: relative;
            .score-wrapper {
                position: relative;
            }
            .score-summary {
                display: flex;
                justify-content: space-between;
                align-items: center;
                .score {
                    font-family: var(--primaryHeader);
                    font-size: 74px;
                    font-weight: 500;
                    line-height: normal;
                                    }
            }
            .score-bar {
                width: 100%;
                height: 12px;
                border: 1px solid var(--asphalt80);
                border-radius: 6px;
                display: flex;
                overflow: hidden;
            }
            .score-fill {
                height: 100%;
                background: var(--volt);
                border-radius: 4px;
            }
            .score-segment {
                height: 100%;
                &:last-child {
                    border-radius: 0 4px 4px 0;
                }
            }
            .score-details {
                display: flex;
                gap: 8px;
                position: absolute;
                padding-top: 6px;

                .score-label {
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    font: var(--editor-ui-b3-regular);
                    font-size: 10px;
                    line-height: 18px;
                    color: var(--asphalt40);

                    .dot {
                        width: 10px;
                        height: 10px;
                        border-radius: 50%;
                        display: inline-block;
                    }
                }
            }
        }

        .overview-container {
            padding: 24px 0;
            border-bottom: 1px solid var(--asphalt80);
        }
    }
}

.title {
    font: var(--editor-ui-b2-semibold);
    margin-bottom: 16px;
}
.list {
    background-color: var(--asphalt90);
    border-radius: 4px;
    padding: 12px;
}
.list-item {
    display: flex;
    gap: 12px;
    font: var(--editor-ui-b2-regular);
    color: var(--asphalt30);
    margin-bottom: 4px;
    svg {
        flex-shrink: 0;
        color: var(--asphalt40);
    }
}

.accordion {
    .accordion-title {
        display: flex;
        justify-content: space-between;
        padding: 24px 0 20px;
        border-bottom: 1px solid var(--asphalt80);
        cursor: pointer;
        color: var(--asphalt30);
        font: var(--editor-ui-b2-semibold);
        &.open {
            border: none;
            color: var(--asphalt15);
        }
        &:hover {
            color: var(--asphalt20);
        }
        svg {
            width: 24px;
            height: 24px;
            color: var(--asphalt40);
        }
    }
    .accordion-body {
        font: var(--editor-ui-b2-regular);

        > div:nth-child(2) {
            padding-top: 16px;
        }
    }
}

.job-description-container {
    display: flex;
    flex-direction: column;
    font: var(--editor-ui-b2-regular);
    color: var(--asphalt40);
    gap: 24px;

    .job-title-and-description {
        display: flex;
        gap: 24px;
        .text-box {
            flex: 1;
        }
    }
    .text-box {
        padding: 14px 16px;
        border: 1px solid var(--asphalt80);
        border-radius: 4px;
        white-space: pre-wrap;
    }
}
</style>